import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Tooltip,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';

/**
 * OmopMappingActions Component
 * Action buttons for OMOP mapping workflows
 * Handles validate, reject, edit, generate, and feedback actions
 *
 * @param {object} props
 * @param {string} props.omopStatus - Current mapping status
 * @param {function} props.onValidate - Callback for validate action
 * @param {function} props.onReject - Callback for reject action
 * @param {function} props.onEdit - Callback for edit/manual mapping action
 * @param {function} props.onGenerateMapping - Callback for AI mapping generation
 * @param {function} props.onOpenCritic - Callback to open feedback dialog
 * @param {boolean} props.isProcessing - Whether an action is in progress
 * @param {boolean} props.hasAiMapping - Whether AI mapping exists
 */
const OmopMappingActions = ({
  omopStatus,
  onValidate,
  onReject,
  onEdit,
  onGenerateMapping,
  onOpenCritic,
  isProcessing = false,
  hasAiMapping = false
}) => {
  // Determine which actions are available based on status
  const showValidateReject = ['mapped', 'pending'].includes(omopStatus) && hasAiMapping;
  const showGenerate = ['unmapped', 'pending'].includes(omopStatus);
  const showCritic = hasAiMapping && omopStatus !== 'validated';

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {/* Validate & Reject Group */}
      {showValidateReject && (
        <ButtonGroup variant="contained" disabled={isProcessing}>
          <Tooltip title="Mapping als korrekt validieren">
            <Button
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={onValidate}
            >
              Validieren
            </Button>
          </Tooltip>
          <Tooltip title="Mapping ablehnen">
            <Button
              color="error"
              startIcon={<CancelIcon />}
              onClick={onReject}
            >
              Ablehnen
            </Button>
          </Tooltip>
        </ButtonGroup>
      )}

      {/* Generate AI Mapping */}
      {showGenerate && (
        <Tooltip title="AI-gestütztes Mapping generieren lassen">
          <Button
            variant="contained"
            startIcon={isProcessing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AutoAwesomeIcon />}
            onClick={onGenerateMapping}
            disabled={isProcessing}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                opacity: 0.6,
              }
            }}
          >
            {isProcessing ? 'Generiere...' : 'KI-Assistent'}
          </Button>
        </Tooltip>
      )}

      {/* AI Improvement Feedback */}
      {showCritic && (
        <Tooltip title="Geben Sie der KI konkrete Hinweise für einen besseren Mapping-Vorschlag">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PsychologyIcon />}
            onClick={onOpenCritic}
            disabled={isProcessing}
          >
            KI-Vorschlag verbessern
          </Button>
        </Tooltip>
      )}
    </Box>
  );
};

export default OmopMappingActions;
